// © Microsoft Corporation. All rights reserved.

import { accentFillRest, DesignSystem, neutralForegroundHint, neutralForegroundRest, neutralLayerCard } from "@microsoft/fast-components-styles-msft";
import { applyLocalizedProperty } from "@microsoft/fast-jss-utilities";
import { avatarSize, dangerColor } from "@msnews/social-utils";

const inputBorderColor = "#DDDDDD";
const inputShadowColor = "rgba(0, 0, 0, 0.02)";
const inputShadowFocusColor = "rgba(0, 0, 0, 0.08)";
const placeholderColor = "#717272";
const clearInputIconColor = "#919191";
const inReviewMarkColor = "#FFB900";
const notAllowMarkColor = "#E22C3B";
const markSize = 20;

export const CommentBoxStyles = (config: DesignSystem) =>  ({
    commentForm: {
        display: "flex",
        "flex-direction": "column",
        margin: "0",

        "& a.disabled": {
            "pointer-events": "none",
            cursor: "default"
        },

        "& .cwt-comment-box-container": {
            display: "flex",
            "align-items": "flex-start",
            position: "relative",
            width: "100%",
            margin: "0",
            padding: "0",
            "border-radius": "4px",

            "& .cwt-avatar-container": {
                position: "relative",
                display: "flex",
                margin: applyLocalizedProperty("0 13px 0 0", "0 0 0 13px", config.direction),
                padding: "3px 0",
                height: avatarSize,

                "& .cwt-commentbox-avatar": {
                    "min-width": avatarSize,
                    "max-width": avatarSize,
                    height: avatarSize,
                    "border-radius": "50%"
                },

                "& .cwt-status-mark": {
                    display: "flex",
                    "align-items": "center",
                    "justify-content": "center",
                    position: "absolute",
                    bottom: (-markSize/4)+"px",
                    [applyLocalizedProperty("right", "left", config.direction)]: (-markSize/4)+"px",
                    width: markSize+"px",
                    height: markSize+"px",
                    color: neutralLayerCard,
                    "border-radius": "50%",
                    border: "2px solid ",
                    "border-color": neutralLayerCard,

                    "&.cwt-inreview": {
                        background: inReviewMarkColor
                    },

                    "&.cwt-notallow": {
                        background: notAllowMarkColor
                    }
                }
            },

            "& .cwt-comment-box": {
                overflow: "auto",
                width: "calc(100% - 90px)",
                "max-height": "480px",
                padding: "12px 16px 11px",
                "font-size": "16px",
                "line-height": "20px",
                background: neutralLayerCard,
                color: neutralForegroundRest,
                border: "1px solid ",
                "border-color": inputBorderColor,
                "border-radius": "8px",
                "-webkit-box-shadow": "0px 4px 4px " + inputShadowColor,
                "-moz-box-shadow": "0px 4px 4px " + inputShadowColor,
                "box-shadow": "0px 4px 4px " + inputShadowColor,
                "vertical-align": "bottom",
                "overflow-wrap": "anywhere",
                "&.cwt-warning": {
                    border: "1px solid ",
                    "border-color": notAllowMarkColor
                },
                // Make it always selectable
                "-webkit-touch-callout": "default",
                "-webkit-user-select": "auto",
                "-khtml-user-select": "auto",
                "-moz-user-select": "auto",
                "-ms-user-select": "auto",
                "user-select": "auto",

                // 'Hide scrollbar'
                "-ms-overflow-style": "none",
                "&::-webkit-scrollbar ": {
                    display: "none"
                },

                "&:focus": {
                    "-webkit-box-shadow": "0px 3px 8px " + inputShadowFocusColor,
                    "-moz-box-shadow": "0px 3px 8px " + inputShadowFocusColor,
                    "box-shadow": "0px 3px 8px " + inputShadowFocusColor,
                    "-webkit-tap-highlight-color": "rgba(255, 255, 255, 0) !important;",
                    outline: "none"
                },

                "&:empty": {
                    display: "block",
                    "&:before": {
                        content: "attr(placeholder)",
                        "pointer-events": "none",
                        display: "block",
                        color: placeholderColor,
                        "line-height": "21px",
                        [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "60px"
                    }
                },

                "&:not(:empty)": {
                    "&:after ": {
                        content: "''",
                        float: applyLocalizedProperty("right", "left", config.direction),
                        display: "block",
                        height: "1.325em",
                        width: "56px"
                    },

                    "& .cwt-name.cwt-link": {
                        "font-weight": "normal",

                        "&:before": {
                            content: "'@'"
                        }
                    }
                },

                '&:not([contentEditable="true"])': {
                    "font-style": "italic",
                    color: "rgba(26, 26, 26, 0.75)",

                    "& .cwt-icon-vector": {
                        fill: neutralForegroundHint
                    },

                    "&:empty ": {
                        "&:before ": {
                            content: "attr(placeholder)",
                            "pointer-events": "none",
                            display: "block",
                            color: placeholderColor
                        }
                    }
                }
            },

            "& .cwt-button-container": {
                position: "absolute",
                [applyLocalizedProperty("right", "left", config.direction)]: "2px",
                bottom: "0",
                height: "46px",
                padding: "0 8px",
                display: "flex",
                "align-items": "center",

                "& .cwt-link": {
                    margin: "0 8px",
                    "font-size": "13px",
                    "font-weight": "500",
                    color: accentFillRest
                },

                "& .cwt-submit-comment-btn": {
                    "& .cwt-icon": {
                        color: accentFillRest
                    },

                    "&:hover": {
                        "& .cwt-icon": {
                            color: "#0b93ff"
                        }
                    }
                },
                "& .cwt-clear-comment-btn": {
                    "& .cwt-icon": {
                        color: clearInputIconColor
                    },

                    "&:hover": {
                        "& .cwt-icon": {
                            color: neutralForegroundHint
                        }
                    }
                }
            }
        },
        "& .cwt-character-remaining-msg": {
            margin: "6px 12px",
            display: "flex",
            "align-items": "center",
            "justify-content": "flex-end",
            "font-size": "12px",
            color: dangerColor
        }
    }
});